import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { getLogByDateAndTime, getLogsByDate, saveLog, getMedications, getProfile } from '../db/database';
import { fetchWeatherData } from '../utils/weather';
import { createEmptyLog, todayString, getVASColor } from '../utils/helpers';
import { SYMPTOM_CATEGORIES, SYMPTOM_LABELS, SEVERITY_LABELS, CROSS_REACTIVE_FOODS } from '../types';
import type { DailyLog } from '../types';

export default function LogWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date') || todayString();
  const timeParam = searchParams.get('time') as DailyLog['entryTime'] | null;

  const [step, setStep] = useState(0);
  const [log, setLog] = useState<DailyLog>(createEmptyLog(dateParam));
  const [savedMeds, setSavedMeds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [isBackdate] = useState(dateParam !== todayString());
  const [existingEntries, setExistingEntries] = useState<DailyLog[]>([]);

  useEffect(() => {
    async function load() {
      // Load existing entries for this date
      const entries = await getLogsByDate(dateParam);
      setExistingEntries(entries);

      // If a specific time param is given, load that entry
      if (timeParam) {
        const existing = await getLogByDateAndTime(dateParam, timeParam);
        if (existing) {
          setLog(existing);
        } else {
          setLog({ ...createEmptyLog(dateParam), entryTime: timeParam });
        }
      } else if (entries.length > 0) {
        // Load the most recent entry for editing
        setLog(entries[entries.length - 1]);
      }

      const meds = await getMedications();
      setSavedMeds(meds.map(m => m.name));
    }
    load();
  }, [dateParam, timeParam]);

  const updateLog = useCallback((updates: Partial<DailyLog>) => {
    setLog(prev => ({ ...prev, ...updates }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Fetch weather data if coordinates available
      const profile = await getProfile();
      let logToSave = { ...log };
      if (profile?.latitude && profile?.longitude) {
        const weather = await fetchWeatherData(profile.latitude, profile.longitude, log.date);
        logToSave = { ...logToSave, ...weather };
      }
      await saveLog(logToSave);
      navigate('/');
    } catch (e) {
      console.error('Save failed:', e);
    }
    setSaving(false);
  };

  const isToday = log.date === todayString();
  const dateLabel = isToday ? 'Oggi' : format(new Date(log.date), 'dd/MM/yyyy');

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => navigate(-1)}
          style={{ padding: '6px 10px', minWidth: 0 }}
        >
          &#8249;
        </button>
        <h1 className="page-title" style={{ marginBottom: 0, flex: 1 }}>
          {log.id ? 'Modifica' : 'Registra'} - {dateLabel}
        </h1>
        {isBackdate && (
          <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>RETRODATATO</span>
        )}
      </div>

      {/* Show existing entries for this date */}
      {existingEntries.length > 0 && !log.id && (
        <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
          Registrazioni esistenti:{' '}
          {existingEntries.map((e, i) => (
            <span key={e.id}>
              {i > 0 && ', '}
              <button
                style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}
                onClick={() => navigate(`/log?date=${dateParam}&time=${e.entryTime}`)}
              >
                {e.entryTime === 'morning' ? 'Mattina' : e.entryTime === 'evening' ? 'Sera' : 'Giornata intera'} (VAS {e.vasScore})
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Wizard steps */}
      <div className="wizard-steps">
        {[0, 1, 2].map(i => (
          <div key={i} className={`wizard-step ${i === step ? 'active' : i < step ? 'completed' : ''}`} />
        ))}
      </div>

      {/* Step 0: VAS */}
      {step === 0 && (
        <div>
          <div className="card">
            <div className="card-title">Come stai con le allergie oggi?</div>
            <div className="slider-value" style={{ color: getVASColor(log.vasScore) }}>
              {log.vasScore}
            </div>
            <div className="slider-container">
              <input
                type="range"
                className="slider"
                min="0"
                max="10"
                step="1"
                value={log.vasScore}
                onChange={e => updateLog({ vasScore: parseInt(e.target.value) })}
              />
            </div>
            <div className="slider-labels">
              <span>0 - Nessun sintomo</span>
              <span>10 - Insopportabile</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
              ARIA: {log.vasScore < 5 ? 'Rinite lieve' : 'Rinite moderata-severa'}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Data e momento</div>
            <div className="form-group">
              <label className="form-label">Data</label>
              <input
                type="date"
                className="form-input"
                value={log.date}
                max={todayString()}
                onChange={e => updateLog({ date: e.target.value })}
              />
            </div>
            <div className="chip-group" style={{ marginTop: 8 }}>
              {(['morning', 'evening', 'single'] as const).map(t => (
                <button
                  key={t}
                  className={`chip ${log.entryTime === t ? 'active' : ''}`}
                  onClick={() => updateLog({ entryTime: t })}
                >
                  {t === 'morning' ? 'Mattina' : t === 'evening' ? 'Sera' : 'Giornata intera'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Symptoms */}
      {step === 1 && (
        <div>
          {Object.entries(SYMPTOM_CATEGORIES).map(([category, symptoms]) => {
            return (
              <div key={category} className="card">
                <div className="card-title">{category}</div>
                {symptoms.map(symptom => (
                  <div key={symptom} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, marginBottom: 6, color: 'var(--text-primary)' }}>
                      {SYMPTOM_LABELS[symptom]}
                    </div>
                    <div className="severity-selector">
                      {[0, 1, 2, 3].map(level => (
                        <button
                          key={level}
                          className={`severity-btn ${(log as any)[symptom] === level ? `selected-${level}` : ''}`}
                          onClick={() => updateLog({ [symptom]: level })}
                        >
                          {SEVERITY_LABELS[level]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {/* ARIA severity */}
          <div className="card">
            <div className="card-title">Impatto sulla qualita' della vita (ARIA)</div>
            {([
              ['affectedSleep', 'I sintomi hanno alterato il sonno?'],
              ['impairedActivities', 'Le attivita\' quotidiane sono state compromesse?'],
              ['impairedWork', 'Il lavoro/studio e\' stato compromesso?'],
              ['troublesome', 'I sintomi sono stati fastidiosi?'],
            ] as const).map(([key, label]) => (
              <div key={key} className="toggle-row">
                <span className="toggle-label">{label}</span>
                <div
                  className={`toggle ${(log as any)[key] ? 'on' : ''}`}
                  onClick={() => updateLog({ [key]: !(log as any)[key] })}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Context */}
      {step === 2 && (
        <div>
          {/* Tier 1 */}
          <div className="card">
            <div className="card-title">Farmaci assunti</div>
            <div className="chip-group">
              {savedMeds.map(med => (
                <button
                  key={med}
                  className={`chip ${log.medicationsTaken.includes(med) ? 'active' : ''}`}
                  onClick={() => {
                    const meds = log.medicationsTaken.includes(med)
                      ? log.medicationsTaken.filter(m => m !== med)
                      : [...log.medicationsTaken, med];
                    updateLog({ medicationsTaken: meds });
                  }}
                >
                  {med}
                </button>
              ))}
              {savedMeds.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Configura i farmaci nelle Impostazioni
                </span>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Sonno e attivita'</div>
            <div className="form-group">
              <label className="form-label">Ore di sonno</label>
              <input
                type="number"
                className="form-input"
                min="0" max="24" step="0.5"
                value={log.sleepHours ?? ''}
                onChange={e => updateLog({ sleepHours: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="7.5"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Ore all'aperto</label>
              <input
                type="number"
                className="form-input"
                min="0" max="24" step="0.5"
                value={log.outdoorHours ?? ''}
                onChange={e => updateLog({ outdoorHours: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="2"
              />
            </div>
          </div>

          {/* Tier 2 */}
          <div className="card">
            <div className="card-title">Contesto ambientale</div>
            <div className="form-group">
              <label className="form-label">Qualita' del sonno (0-10)</label>
              <input
                type="range" className="slider" min="0" max="10"
                value={log.sleepQuality ?? 5}
                onChange={e => updateLog({ sleepQuality: parseInt(e.target.value) })}
                style={{ background: 'linear-gradient(to right, var(--danger), var(--success))' }}
              />
              <div className="slider-labels">
                <span>Pessimo</span>
                <span>{log.sleepQuality ?? '-'}</span>
                <span>Ottimo</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Livello di stress (0-10)</label>
              <input
                type="range" className="slider" min="0" max="10"
                value={log.stressLevel ?? 3}
                onChange={e => updateLog({ stressLevel: parseInt(e.target.value) })}
              />
              <div className="slider-labels">
                <span>Rilassato</span>
                <span>{log.stressLevel ?? '-'}</span>
                <span>Molto stressato</span>
              </div>
            </div>

            {/* Toggles */}
            <div className="toggle-row">
              <span className="toggle-label">Aria condizionata accesa</span>
              <div className={`toggle ${log.acOn ? 'on' : ''}`} onClick={() => updateLog({ acOn: !log.acOn })} />
            </div>
            {log.acOn && (
              <div className="chip-group" style={{ marginTop: 8, marginBottom: 8 }}>
                {['ricircolo', 'aria esterna'].map(mode => (
                  <button
                    key={mode}
                    className={`chip ${log.acMode === mode ? 'active' : ''}`}
                    onClick={() => updateLog({ acMode: mode })}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            )}

            <div className="toggle-row">
              <span className="toggle-label">Finestre aperte (mare)</span>
              <div className={`toggle ${log.windowsOpenSea ? 'on' : ''}`} onClick={() => updateLog({ windowsOpenSea: !log.windowsOpenSea })} />
            </div>
            {log.windowsOpenSea && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">Durata finestre mare (minuti)</label>
                <input type="number" className="form-input" min="0"
                  value={log.windowsSeaDuration ?? ''} placeholder="60"
                  onChange={e => updateLog({ windowsSeaDuration: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
            )}

            <div className="toggle-row">
              <span className="toggle-label">Finestre aperte (strada)</span>
              <div className={`toggle ${log.windowsOpenStreet ? 'on' : ''}`} onClick={() => updateLog({ windowsOpenStreet: !log.windowsOpenStreet })} />
            </div>
            {log.windowsOpenStreet && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">Durata finestre strada (minuti)</label>
                <input type="number" className="form-input" min="0"
                  value={log.windowsStreetDuration ?? ''} placeholder="60"
                  onChange={e => updateLog({ windowsStreetDuration: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
            )}

            <div className="toggle-row">
              <span className="toggle-label">Viaggio in auto</span>
              <div className={`toggle ${log.carTravel ? 'on' : ''}`} onClick={() => updateLog({ carTravel: !log.carTravel })} />
            </div>

            <div className="toggle-row">
              <span className="toggle-label">Esercizio all'aperto</span>
              <div className={`toggle ${log.exerciseOutdoor ? 'on' : ''}`} onClick={() => updateLog({ exerciseOutdoor: !log.exerciseOutdoor })} />
            </div>

            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">Ambiente esterno</label>
              <div className="chip-group">
                {['parco', 'citta\'', 'campagna', 'mare'].map(env => (
                  <button key={env} className={`chip ${log.outdoorEnvironment.includes(env) ? 'active' : ''}`}
                    onClick={() => {
                      const envs = log.outdoorEnvironment.includes(env)
                        ? log.outdoorEnvironment.filter(e => e !== env)
                        : [...log.outdoorEnvironment, env];
                      updateLog({ outdoorEnvironment: envs });
                    }}>
                    {env}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Alcol</label>
              <div className="chip-group">
                {[['', 'Nessuno'], ['vino_rosso', 'Vino rosso'], ['vino_bianco', 'Vino bianco'], ['birra', 'Birra'], ['superalcolici', 'Superalcolici']].map(([val, label]) => (
                  <button key={val} className={`chip ${log.alcoholType === val ? 'active' : ''}`}
                    onClick={() => updateLog({ alcoholType: val })}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tier 3 */}
          <div className="card">
            <div className="card-title">Dettagli aggiuntivi</div>
              <div className="toggle-row">
                <span className="toggle-label">Contatto con animali</span>
                <div className={`toggle ${log.petContact ? 'on' : ''}`} onClick={() => updateLog({ petContact: !log.petContact })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Bucato steso all'aperto</span>
                <div className={`toggle ${log.laundryOutdoor ? 'on' : ''}`} onClick={() => updateLog({ laundryOutdoor: !log.laundryOutdoor })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Pulizie effettuate</span>
                <div className={`toggle ${log.cleaningDone ? 'on' : ''}`} onClick={() => updateLog({ cleaningDone: !log.cleaningDone })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Pulita sabbietta</span>
                <div className={`toggle ${log.cleanedLitterBox ? 'on' : ''}`} onClick={() => updateLog({ cleanedLitterBox: !log.cleanedLitterBox })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Graffiato da Misù</span>
                <div className={`toggle ${log.scratchedByMisu ? 'on' : ''}`} onClick={() => updateLog({ scratchedByMisu: !log.scratchedByMisu })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Manipolato cartone/carta vecchia</span>
                <div className={`toggle ${log.handledCardboard ? 'on' : ''}`} onClick={() => updateLog({ handledCardboard: !log.handledCardboard })} />
              </div>
              <div className="toggle-row">
                <span className="toggle-label">Esposizione a fumo</span>
                <div className={`toggle ${log.smokingExposure ? 'on' : ''}`} onClick={() => updateLog({ smokingExposure: !log.smokingExposure })} />
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Cibi cross-reattivi consumati</label>
                <div className="chip-group">
                  {CROSS_REACTIVE_FOODS.map(food => (
                    <button key={food}
                      className={`chip ${log.crossReactiveFoods.includes(food) ? 'active' : ''}`}
                      onClick={() => {
                        const foods = log.crossReactiveFoods.includes(food)
                          ? log.crossReactiveFoods.filter(f => f !== food)
                          : [...log.crossReactiveFoods, food];
                        updateLog({ crossReactiveFoods: foods });
                      }}>
                      {food}
                    </button>
                  ))}
                </div>
              </div>
          </div>

          {/* Notes */}
          <div className="card">
            <div className="card-title">Note</div>
            <textarea
              className="form-textarea"
              value={log.notes}
              onChange={e => updateLog({ notes: e.target.value })}
              placeholder="Annotazioni libere..."
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="wizard-nav">
        {step > 0 && (
          <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
            Indietro
          </button>
        )}
        {step < 2 ? (
          <button className="btn btn-primary" onClick={() => setStep(step + 1)}>
            Avanti
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        )}
      </div>
    </div>
  );
}
